
import Vue from 'vue';
import gsap from '~/plugins/gsap';
import { transformLink } from '~/transforms/transformComponents';

export default Vue.extend({
  props: {
    showAlways: {
      type: Boolean,
      default: false,
    },
    theme: {
      type: String,
      default: 'green',
    },
    items: {
      type: Array as () => Array<{
        id: string;
        isPermanent: boolean;
        isWarning: boolean;
        isShown: boolean;
        headline: string;
        copy: string;
        pageSlugs: string[];
        link: {
          url: string;
          label: string;
        };
        backgroundColor?: string | null;
      }>,
      default: () => [],
    },
  },
  data() {
    return {
      isVisible: false,
      hasAnimated: false,
      visibleItems: this.items,
      activeIndex: 0,
      flickityOptions: {
        wrapAround: true,
        prevNextButtons: false,
        pageDots: true,
        adaptiveHeight: false,
        autoPlay: 4000,
      },
    };
  },
  computed: {
    activeBackgroundColor(): string | null {
      return (this as any).visibleItems[(this as any).activeIndex]?.backgroundColor ?? null;
    },
    // Returns true if the activeBackgroundColor is very dark
    activeTextColorIsWhite(): boolean {
      const color = this.activeBackgroundColor;
      if (!color) return false;

      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);

      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      return brightness < 90;
    },
    // Returns true if the activeBackgroundColor is close to the (red) link color, in which case
    // the link needs to be switched to white to stay readable, regardless of overall brightness.
    activeLinkColorIsWhite(): boolean {
      const color = this.activeBackgroundColor;
      if (!color) return false;

      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);

      // Reference red used for the link/primary color (#e6142c)
      const linkR = 230;
      const linkG = 20;
      const linkB = 44;

      const distance = Math.sqrt((r - linkR) ** 2 + (g - linkG) ** 2 + (b - linkB) ** 2);
      return distance < 120;
    },
  },
  methods: {
    checkVisibility() {
      if (!this.items.length) {
        this.isVisible = false;
        return;
      }
      const alertsStorage = sessionStorage.getItem('alertsClosed');

      let alertsClosed: any[] = [];
      alertsClosed = (alertsStorage && JSON.parse(alertsStorage)) || [];

      if (this.showAlways) {
        this.visibleItems = this.items.filter(
          item =>
            alertsClosed.some(closed => {
              if (item.isPermanent && closed.id === item.id) {
                closed.isShown = true;
                return true;
              }
              return false;
            }) ||
            (item.isPermanent && item.pageSlugs.length === 0)
        );

        if (!this.visibleItems.length) {
          this.isVisible = false;
          return;
        }
        this.isVisible = true;
        sessionStorage.setItem('alertsClosed', JSON.stringify(alertsClosed));

        return;
      }
      this.visibleItems = this.items.filter(item => {
        return (
          (item.pageSlugs.some(page => page === this.$route.path.replace(/\/$/, '')) || item.pageSlugs.length === 0) &&
          !alertsClosed.some(closed => closed.id === item.id)
        );
      });

      if (this.visibleItems.length === 0) {
        this.isVisible = false;
        return;
      }

      this.isVisible = true;
    },

    closeAlert() {
      const alertsStorage = sessionStorage.getItem('alertsClosed');

      let alertsClosed = (alertsStorage && JSON.parse(alertsStorage)) || [];

      this.visibleItems.forEach(item => {
        if (item.isPermanent) this.$root.$emit('setIndicator');
        alertsClosed.push(item);
      });

      sessionStorage.setItem('alertsClosed', JSON.stringify(alertsClosed));

      this.isVisible = false;
      this.visibleItems = [];
    },

    transformLink(link: { url: string; label: string }) {
      return transformLink(link, link?.label);
    },

    initAnimation() {
      if (!process.client) return;

      gsap.config({ nullTargetWarn: false });

      // ---> set module visible

      const module = this.$el as HTMLElement;
      gsap.to(module, { opacity: 1, duration: 0.1, ease: 'curveAppear' });

      // ---> clipPath

      const clipPathList = this.$el.querySelectorAll('#bm-alert__bg-shape-clipPath path') as NodeList;

      // gsap.set(clipPathList[0], { rotation: 180 })
      // gsap.set(clipPathList[1], { rotation: -180 })
      const alertContent = this.$el.querySelector('.bm-alert__content') as HTMLElement;
      const alertClose = this.$el.querySelector('.bm-alert__close') as HTMLElement;
      gsap.set(alertContent, { opacity: 0 });
      gsap.set(alertClose, { opacity: 0 });

      // gsap.set(clipPathList[0], { rotation: 180 })
      // gsap.set(clipPathList[1], { rotation: -180 })
      gsap.set(clipPathList, { rotation: -180 });

      gsap.to(alertContent, { opacity: 1, duration: 0.6, delay: 0.8 });
      gsap.to(alertClose, { opacity: 1, delay: 0.8, duration: 0.2 });

      gsap.set(clipPathList, { rotation: -180 });
      gsap.set(clipPathList[1], { autoAlpha: 0 });
      gsap.set(clipPathList[1], { autoAlpha: 1, delay: 0.6 });

      gsap.to(clipPathList[0], { rotation: 0, duration: 0.6, ease: 'curveTech', force3D: true });
      gsap.to(clipPathList[1], { rotation: 0, duration: 0.6, ease: 'curveTech', force3D: true, delay: 0.2 });
    },
  },
  mounted() {
    if (this.showAlways) {
      this.checkVisibility();
    } else {
      setTimeout(() => {
        this.checkVisibility();
      }, 1200);
    }
  },
  updated() {
    if (this.$refs.flickity) {
      if (!this.hasAnimated) {
        this.initAnimation();
        this.hasAnimated = true;
      }
      (this.$refs.flickity as any).resize();
      (this.$refs.flickity as any).off('change');
      (this.$refs.flickity as any).on('change', (index: number) => {
        this.activeIndex = index;
      });
    }
  },
});
